import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building, ArrowLeft, Save, Plus, Trash2, MapPin, Phone, Mail, Globe,
  Loader2, FileText, Edit2, X, Check
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { empresaAPI } from '../api';
import { toast } from '../hooks/use-toast';

const ConfiguracionEmpresa = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Datos de la empresa
  const [empresa, setEmpresa] = useState({
    nombre: '',
    cif: '',
    email: '',
    web: '',
    notas: '',
    direcciones: [],
    telefonos: []
  });
  
  // Diálogos
  const [isDireccionDialogOpen, setIsDireccionDialogOpen] = useState(false);
  const [isTelefonoDialogOpen, setIsTelefonoDialogOpen] = useState(false);
  const [editingDireccionIndex, setEditingDireccionIndex] = useState(null);
  const [editingTelefonoIndex, setEditingTelefonoIndex] = useState(null);
  
  // Formularios
  const [direccionForm, setDireccionForm] = useState({
    tipo: 'Principal',
    direccion: '',
    ciudad: '',
    cp: '',
    provincia: '',
    pais: 'España'
  });
  
  const [telefonoForm, setTelefonoForm] = useState({
    tipo: 'Principal',
    numero: '',
    extension: ''
  });

  // Tipos predefinidos
  const tiposDireccion = ['Principal', 'Secundaria', 'Fiscal', 'Correspondencia', 'Otra'];
  const tiposTelefono = ['Principal', 'Móvil', 'Fijo', 'Fax', 'Otro'];

  useEffect(() => {
    loadEmpresa();
  }, []);

  const loadEmpresa = async () => {
    try {
      setLoading(true);
      const response = await empresaAPI.get();
      setEmpresa(response.data);
    } catch (error) {
      toast({ title: "Error", description: "No se pudieron cargar los datos de la empresa", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!empresa.nombre?.trim()) {
      toast({ title: "Error", description: "El nombre de la empresa es obligatorio", variant: "destructive" });
      return;
    }
    
    try {
      setSaving(true);
      await empresaAPI.update(empresa);
      toast({ title: "Datos guardados correctamente" });
    } catch (error) {
      toast({ title: "Error", description: "No se pudieron guardar los datos", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  // ============== DIRECCIONES ==============
  
  const openDireccionDialog = (index = null) => {
    if (index !== null) {
      setEditingDireccionIndex(index);
      setDireccionForm({ ...empresa.direcciones[index] });
    } else {
      setEditingDireccionIndex(null);
      setDireccionForm({
        tipo: 'Principal',
        direccion: '',
        ciudad: '',
        cp: '',
        provincia: '',
        pais: 'España'
      });
    }
    setIsDireccionDialogOpen(true);
  };

  const handleSaveDireccion = () => {
    if (!direccionForm.direccion?.trim()) {
      toast({ title: "Error", description: "La dirección es obligatoria", variant: "destructive" });
      return;
    }
    
    const nuevasDirecciones = [...empresa.direcciones];
    if (editingDireccionIndex !== null) {
      nuevasDirecciones[editingDireccionIndex] = direccionForm;
    } else {
      nuevasDirecciones.push(direccionForm);
    }
    
    setEmpresa({ ...empresa, direcciones: nuevasDirecciones });
    setIsDireccionDialogOpen(false);
    toast({ title: editingDireccionIndex !== null ? "Dirección actualizada" : "Dirección añadida" });
  };

  const handleDeleteDireccion = (index) => {
    const nuevasDirecciones = empresa.direcciones.filter((_, i) => i !== index);
    setEmpresa({ ...empresa, direcciones: nuevasDirecciones });
    toast({ title: "Dirección eliminada" });
  };

  // ============== TELÉFONOS ==============
  
  const openTelefonoDialog = (index = null) => {
    if (index !== null) {
      setEditingTelefonoIndex(index);
      setTelefonoForm({ ...empresa.telefonos[index] });
    } else {
      setEditingTelefonoIndex(null);
      setTelefonoForm({
        tipo: 'Principal',
        numero: '',
        extension: ''
      });
    }
    setIsTelefonoDialogOpen(true);
  };

  const handleSaveTelefono = () => {
    if (!telefonoForm.numero?.trim()) {
      toast({ title: "Error", description: "El número es obligatorio", variant: "destructive" });
      return;
    }
    
    const nuevosTelefonos = [...empresa.telefonos];
    if (editingTelefonoIndex !== null) {
      nuevosTelefonos[editingTelefonoIndex] = telefonoForm;
    } else {
      nuevosTelefonos.push(telefonoForm);
    }
    
    setEmpresa({ ...empresa, telefonos: nuevosTelefonos });
    setIsTelefonoDialogOpen(false);
    toast({ title: editingTelefonoIndex !== null ? "Teléfono actualizado" : "Teléfono añadido" });
  };

  const handleDeleteTelefono = (index) => {
    const nuevosTelefonos = empresa.telefonos.filter((_, i) => i !== index);
    setEmpresa({ ...empresa, telefonos: nuevosTelefonos });
    toast({ title: "Teléfono eliminado" });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/configuracion')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Building className="w-6 h-6" />
              Gestión de Empresa
            </h1>
            <p className="text-gray-500">Configura los datos de tu despacho</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar cambios
        </Button>
      </div>

      <div className="grid gap-6">
        {/* Datos Básicos */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Datos Básicos
            </CardTitle>
            <CardDescription>Información principal de la empresa</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre de la empresa *</Label>
                <Input
                  id="nombre"
                  value={empresa.nombre}
                  onChange={(e) => setEmpresa({ ...empresa, nombre: e.target.value })}
                  placeholder="Nombre del despacho o empresa"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cif">CIF / NIF</Label>
                <Input
                  id="cif"
                  value={empresa.cif || ''}
                  onChange={(e) => setEmpresa({ ...empresa, cif: e.target.value })}
                  placeholder="B12345678"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email principal de contacto</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    value={empresa.email || ''}
                    onChange={(e) => setEmpresa({ ...empresa, email: e.target.value })}
                    placeholder="contacto@despacho.com"
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="web">Página web</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="web"
                    value={empresa.web || ''}
                    onChange={(e) => setEmpresa({ ...empresa, web: e.target.value })}
                    placeholder="https://www.despacho.com"
                    className="pl-10"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notas">Notas adicionales</Label>
              <Textarea
                id="notas"
                value={empresa.notas || ''}
                onChange={(e) => setEmpresa({ ...empresa, notas: e.target.value })}
                placeholder="Información adicional sobre la empresa..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Direcciones */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Direcciones
                </CardTitle>
                <CardDescription>Sedes y direcciones físicas del despacho</CardDescription>
              </div>
              <Button onClick={() => openDireccionDialog()}>
                <Plus className="w-4 h-4 mr-2" />
                Añadir dirección
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {empresa.direcciones?.length > 0 ? (
              <div className="space-y-3">
                {empresa.direcciones.map((dir, index) => (
                  <div
                    key={index}
                    className="flex items-start justify-between p-4 bg-gray-50 rounded-lg border hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline">{dir.tipo}</Badge>
                      </div>
                      <p className="font-medium">{dir.direccion}</p>
                      <p className="text-sm text-gray-500">
                        {[dir.cp, dir.ciudad, dir.provincia, dir.pais].filter(Boolean).join(', ')}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openDireccionDialog(index)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleDeleteDireccion(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <MapPin className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No hay direcciones registradas</p>
                <Button variant="link" onClick={() => openDireccionDialog()}>
                  Añadir primera dirección
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Teléfonos */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Teléfonos
                </CardTitle>
                <CardDescription>Números de contacto del despacho</CardDescription>
              </div>
              <Button onClick={() => openTelefonoDialog()}>
                <Plus className="w-4 h-4 mr-2" />
                Añadir teléfono
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {empresa.telefonos?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {empresa.telefonos.map((tel, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-full">
                        <Phone className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">{tel.numero}{tel.extension ? ` ext. ${tel.extension}` : ''}</p>
                        <Badge variant="outline" className="text-xs">{tel.tipo}</Badge>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openTelefonoDialog(index)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleDeleteTelefono(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Phone className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No hay teléfonos registrados</p>
                <Button variant="link" onClick={() => openTelefonoDialog()}>
                  Añadir primer teléfono
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog Dirección */}
      <Dialog open={isDireccionDialogOpen} onOpenChange={setIsDireccionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingDireccionIndex !== null ? 'Editar dirección' : 'Nueva dirección'}
            </DialogTitle>
            <DialogDescription>
              Introduce los datos de la dirección física
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tipo de dirección</Label>
              <Select
                value={direccionForm.tipo}
                onValueChange={(v) => setDireccionForm({ ...direccionForm, tipo: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tiposDireccion.map((tipo) => (
                    <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Dirección *</Label>
              <Input
                value={direccionForm.direccion}
                onChange={(e) => setDireccionForm({ ...direccionForm, direccion: e.target.value })}
                placeholder="Calle, número, piso..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Código Postal</Label>
                <Input
                  value={direccionForm.cp || ''}
                  onChange={(e) => setDireccionForm({ ...direccionForm, cp: e.target.value })}
                  placeholder="08001"
                />
              </div>
              <div className="space-y-2">
                <Label>Ciudad</Label>
                <Input
                  value={direccionForm.ciudad || ''}
                  onChange={(e) => setDireccionForm({ ...direccionForm, ciudad: e.target.value })}
                  placeholder="Barcelona"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Provincia</Label>
                <Input
                  value={direccionForm.provincia || ''}
                  onChange={(e) => setDireccionForm({ ...direccionForm, provincia: e.target.value })}
                  placeholder="Barcelona"
                />
              </div>
              <div className="space-y-2">
                <Label>País</Label>
                <Input
                  value={direccionForm.pais || 'España'}
                  onChange={(e) => setDireccionForm({ ...direccionForm, pais: e.target.value })}
                  placeholder="España"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDireccionDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveDireccion}>
              <Check className="w-4 h-4 mr-2" />
              {editingDireccionIndex !== null ? 'Guardar' : 'Añadir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Teléfono */}
      <Dialog open={isTelefonoDialogOpen} onOpenChange={setIsTelefonoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingTelefonoIndex !== null ? 'Editar teléfono' : 'Nuevo teléfono'}
            </DialogTitle>
            <DialogDescription>
              Introduce los datos del número de teléfono
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tipo de teléfono</Label>
              <Select
                value={telefonoForm.tipo}
                onValueChange={(v) => setTelefonoForm({ ...telefonoForm, tipo: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tiposTelefono.map((tipo) => (
                    <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Número *</Label>
              <Input
                value={telefonoForm.numero}
                onChange={(e) => setTelefonoForm({ ...telefonoForm, numero: e.target.value })}
                placeholder="+34 93 000 00 00"
              />
            </div>
            <div className="space-y-2">
              <Label>Extensión (opcional)</Label>
              <Input
                value={telefonoForm.extension || ''}
                onChange={(e) => setTelefonoForm({ ...telefonoForm, extension: e.target.value })}
                placeholder="123"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTelefonoDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveTelefono}>
              <Check className="w-4 h-4 mr-2" />
              {editingTelefonoIndex !== null ? 'Guardar' : 'Añadir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfiguracionEmpresa;
